import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

// ---- Dialog -------------------------------------------------------------------------------------

export function Dialog({ title, children, foot, onClose, wide }: { title: string; children: ReactNode; foot?: ReactNode; onClose: () => void; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input, select, textarea, button.primary, button");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="win-title">
          <span>{title}</span>
          <div className="actions"><button className="btn ghost icon" aria-label="Close" title="Close (Esc)" onClick={onClose}><Icon name="close" /></button></div>
        </div>
        <div className="dialog-body">{children}</div>
        {foot && <div className="dialog-foot">{foot}</div>}
      </div>
    </div>
  );
}

// ---- Toasts -------------------------------------------------------------------------------------

export interface ToastAction { label: string; run: () => void }
type Toast = { id: number; text: string; tone: "info" | "ok" | "err"; action?: ToastAction };
type Push = (text: string, tone?: Toast["tone"], action?: ToastAction) => void;
const ToastContext = createContext<Push>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback<Push>((text, tone = "info", action) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-3), { id, text, tone, action }]);
    // An undoable toast stays a little longer: the time it takes to notice and reach for it.
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === "err" ? 7000 : action ? 6000 : 3500);
  }, []);
  const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <span className="grow">{t.text}</span>
            {t.action && <button className="btn" onClick={() => { t.action!.run(); dismiss(t.id); }}>{t.action.label}</button>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// ---- Icons (inline, 14px, currentColor) ----------------------------------------------------------

const ICONS = {
  plus: <path d="M8 3v10M3 8h10" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  trash: <path d="M3 4.5h10M6.5 4.5V3h3v1.5M5 4.5l.6 8.5h4.8l.6-8.5" />,
  download: <path d="M8 2.5v8M4.8 7.3L8 10.5l3.2-3.2M3 13.5h10" />,
  send: <path d="M2.5 8l11-5.5-3 11-2.8-4.2L2.5 8z" />,
  back: <path d="M10 3L5 8l5 5" />,
  search: <path d="M7 11.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zM10.3 10.3L13.5 13.5" />,
  // Accountant panel navigation
  dashboard: <path d="M2.5 2.5h4.5v5H2.5zM9 2.5h4.5v3H9zM9 7.5h4.5v6H9zM2.5 9.5h4.5v4H2.5z" />,
  clients: <path d="M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM1.8 13.5c.4-2.4 2-3.8 4.2-3.8s3.8 1.4 4.2 3.8M10.5 2.8a2.3 2.3 0 0 1 0 4.4M12 9.9c1.2.5 2 1.7 2.2 3.6" />,
  inbox: <path d="M2.5 9l1.8-6h7.4l1.8 6v4.5h-11zM2.5 9h3.2l.8 1.8h3l.8-1.8h3.2" />,
  calendar: <path d="M2.5 3.5h11v10h-11zM2.5 6.5h11M5.5 2v3M10.5 2v3M5 9h1.5M7.8 9h1.5M10.5 9H12M5 11.3h1.5M7.8 11.3h1.5" />,
  w9: <path d="M3.5 1.5h6l3 3v10h-9zM9.5 1.5v3h3M5.5 8h5M5.5 10.3h5M5.5 12.5h3" />,
  ledger: <path d="M2.5 2.5h11v11h-11zM2.5 5.5h11M6 5.5v8M8.5 8h3M8.5 10.5h3" />,
  car: <path d="M2 11V8.3l1.6-3.8h8.8L14 8.3V11zM2 8.3h12M4.5 11v1.8M11.5 11v1.8M4.5 9.6h.01M11.5 9.6h.01" />,
  form: <path d="M3 1.5h10v13H3zM5.5 4.5h5M5.5 7h5M5.5 9.5h2.5M10 9.2l.8.8 1.7-1.9M5.5 12h5" />,
  auto: <path d="M6.5 2l1.1 3.2L10.8 6.3 7.6 7.4 6.5 10.6 5.4 7.4 2.2 6.3l3.2-1.1zM11.8 9.2l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" />,
  settings: <path d="M8 10.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4zM8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />,
};
export type IconName = keyof typeof ICONS;

export function Icon({ name }: { name: IconName }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

// ---- Small pieces -------------------------------------------------------------------------------

export const Spinner = () => <span className="spinner" aria-label="Loading" />;

export function Banner({ tone = "info", children, action }: { tone?: "info" | "warn" | "err" | "ok"; children: ReactNode; action?: ReactNode }) {
  return <div className={`banner ${tone}`} role={tone === "err" ? "alert" : undefined}><div className="grow">{children}</div>{action}</div>;
}

/** An empty state always says what happens next; `action` is the button or link that does it. */
export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children && <div>{children}</div>}{action && <div className="act">{action}</div>}</div>;
}

export function Field({ label, children, error }: { label: string; children: ReactNode; error?: string | null }) {
  return <label className="field"><span>{label}</span>{children}{error && <span className="err">{error}</span>}</label>;
}

export function Win({ title, actions, children, flush }: { title: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className="win">
      <div className="win-title"><span>{title}</span>{actions && <div className="actions">{actions}</div>}</div>
      <div className={`win-body${flush ? " flush" : ""}`}>{children}</div>
    </section>
  );
}

/** A button that runs an async action, shows it's busy, and reports failure instead of swallowing it. */
export function AsyncButton({ onRun, children, className = "btn", disabled, title, onError }: {
  onRun: () => Promise<unknown>; children: ReactNode; className?: string; disabled?: boolean; title?: string; onError?: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <button className={className} disabled={disabled || busy} title={title} onClick={async () => {
      setBusy(true);
      try {
        await onRun();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Something went wrong.";
        if (onError) onError(message); else toast(message, "err");
      } finally {
        setBusy(false);
      }
    }}>{busy ? <Spinner /> : null}{children}</button>
  );
}

// ---- Keyboard -----------------------------------------------------------------------------------

/** Single keys and "g x" sequences, ignored while typing in a field or with a dialog open. */
export function useHotkeys(map: Record<string, () => void>, enabled = true) {
  const pending = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target instanceof Element ? e.target : null;
      if (t?.closest("input, textarea, select, [contenteditable=true]") || document.querySelector(".overlay, .viewer")) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      // Enter on a focused button, link or row belongs to that element, not to a list shortcut.
      if (key === "Enter" && t?.closest("button, a[href], tr[tabindex], [role=button]")) return;
      const combo = pending.current ? `${pending.current} ${key}` : key;
      if (map[combo]) {
        e.preventDefault();
        pending.current = null;
        map[combo]();
        return;
      }
      if (Object.keys(map).some((k) => k.startsWith(`${key} `))) {
        pending.current = key;
        window.setTimeout(() => (pending.current = null), 900);
        return;
      }
      pending.current = null;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [map, enabled]);
}

/** J/K to move, Enter to open, plus any extra single-key actions on the selected row. */
export function useListKeys<T>(items: T[], onOpen: (item: T) => void, extra: Record<string, (item: T) => void> = {}, enabled = true) {
  const [sel, setSel] = useState(0);
  useEffect(() => { if (sel >= items.length) setSel(Math.max(0, items.length - 1)); }, [items.length, sel]);
  const map = useMemo(() => {
    const m: Record<string, () => void> = {
      j: () => setSel((s) => Math.min(s + 1, items.length - 1)),
      k: () => setSel((s) => Math.max(s - 1, 0)),
      Enter: () => items[sel] && onOpen(items[sel]),
    };
    for (const [key, fn] of Object.entries(extra)) m[key] = () => items[sel] && fn(items[sel]);
    return m;
  }, [items, sel, onOpen, extra]);
  useHotkeys(map, enabled);
  return { sel, setSel };
}

/** The `?` cheat sheet: groups of [key, what it does]. */
export type ShortcutGroup = { title: string; keys: [string, string][] };
export function ShortcutList({ groups }: { groups: ShortcutGroup[] }) {
  return (
    <div className="shortcut-list">
      {groups.map((g) => (
        <Fragment key={g.title}>
          <div className="head">{g.title}</div>
          {g.keys.map(([k, v]) => <Fragment key={k}><kbd>{k}</kbd><span>{v}</span></Fragment>)}
        </Fragment>
      ))}
    </div>
  );
}

export function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  ta.remove();
  return Promise.resolve();
}
